import { useState, useContext } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Mail, Send, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const Contact = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    userId: user?.id || 'Not logged in',
    email: user?.email || '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      toast.error('Please enter your message');
      return;
    }

    setSending(true);

    // Create mailto link with form data
    const subject = encodeURIComponent('CultraMeet Support Request');
    const body = encodeURIComponent(
      `User ID: ${formData.userId}\n` +
      `Email: ${formData.email}\n` +
      `\nMessage:\n${formData.message}`
    );
    
    const mailtoLink = `mailto:cultrameet@gmail.com?subject=${subject}&body=${body}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    setTimeout(() => {
      setSending(false);
      toast.success('Opening your email client...');
      // Clear message after sending
      setFormData({ ...formData, message: '' });
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 py-8 pt-24">
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center space-x-2 text-gray-600 hover:text-coral transition-colors mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Settings</span>
        </button>

        <div className="glass-dark rounded-3xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-white border-2 border-teal rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-black" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Contact Support</h1>
            <p className="text-gray-600">We're here to help! Send us your questions or feedback.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* User ID */}
            <div>
              <Label htmlFor="userId" className="text-gray-700 font-medium">
                User ID
              </Label>
              <Input
                id="userId"
                type="text"
                value={formData.userId}
                readOnly
                className="mt-1 bg-gray-100 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                This helps us identify your account quickly
              </p>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium">
                Your Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your.email@example.com"
                className="mt-1"
                required
              />
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message" className="text-gray-700 font-medium">
                Detailed Description
              </Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Please describe your issue or feedback in detail. Include any relevant information that might help us assist you better..."
                className="mt-1 min-h-[200px] resize-y"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Tip: Include screenshots or specific details for faster resolution
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={sending}
              className="w-full py-6 rounded-xl text-lg font-semibold bg-white text-black border-2 border-teal hover:bg-teal hover:text-white"
            >
              {sending ? (
                'Opening Email Client...'
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </form>

          {/* Support Info */}
          <div className="mt-8 p-4 bg-teal-50 rounded-xl border border-teal-200">
            <p className="text-sm text-center text-gray-700">
              <strong>Direct Email:</strong>{' '}
              <a href="mailto:cultrameet@gmail.com" className="text-teal-600 hover:text-teal-700 font-medium">
                cultrameet@gmail.com
              </a>
            </p>
            <p className="text-xs text-center text-gray-600 mt-2">
              We typically respond within 24-48 hours
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
